import React, { useState } from 'react';
import { useVerifyUserEmail, useLoginUser } from '../../api/users';
import { useUserContext } from '../../context/UserContext';
import { useNotifications } from '../../context/NotificationContext';
import { setAccessToken } from '../../api/client';
import '../../components/login/LoginModal.css';

interface CheckoutLoginProps {
  onLoginSuccess?: () => void;
  onRegisterClick?: () => void;
  onForgotPasswordClick?: () => void;
}

const CheckoutLogin: React.FC<CheckoutLoginProps> = ({ onLoginSuccess, onRegisterClick, onForgotPasswordClick }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const verifyUserMutation = useVerifyUserEmail();
  const loginMutation = useLoginUser();
  const { isLoggedIn, user, signInWithGoogle, logout, login } = useUserContext();
  const { showError } = useNotifications();

  const handleEmailSubmit = async () => {
    setErrorMessage('');

    if (email.trim()) {
      try {
        const result = await verifyUserMutation.mutateAsync({
          email: email.trim(),
        });

        if (result.is_verified) {
          setShowPassword(true);
        } else {
          setErrorMessage('Please verify your email first or register for a new account.');
        }
      } catch (error: any) {
        // Handle different error formats from axios/apiClient
        const status = error?.response?.status || error?.status;
        const message = error?.message || '';

        if (status === 404 || message.includes('404') || message.toLowerCase().includes('not found')) {
          setErrorMessage('No account found with this email. Please register or continue as a guest.');
        } else {
          setErrorMessage('Something went wrong. Please try again.');
        }
      }
    }
  };

  const handlePasswordSubmit = async () => {
    setErrorMessage('');

    try {
      const result = await loginMutation.mutateAsync({
        email: email.trim(),
        password: password,
      });

      if (result.access_token) {
        setAccessToken(result.access_token);
        login(result.user);
        onLoginSuccess?.();
      }
    } catch (error: any) {
      setErrorMessage('Invalid password. Please try again.');
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    try {
      await signInWithGoogle();
      onLoginSuccess?.();
    } catch (error: any) {
      showError('Failed to sign in with Google. Please try again.', error instanceof Error ? error : new Error('Google sign-in failed'));
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    setEmail('');
    setPassword('');
    setShowPassword(false);
  };

  if (isLoggedIn && user) {
    return (
      <></>
    );
  }

  return (
    <section className="checkout-section-card">
      <h2 className="checkout-section-title">Already have an account?</h2>
      <div className="checkout-login">
        <p className="checkout-login-description">
          Log in to quickly pre-fill your information and manage your bookings. You can also skip this step and book your camp as a guest.
        </p>

        {errorMessage && (
          <div className="message message--error">
            {errorMessage}
          </div>
        )}

        {!showPassword ? (
          <div className="login-form">
            <div className="form-group">
              <label htmlFor="checkout-email">Email</label>
              <input
                type="email"
                id="checkout-email"
                className="form-input"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleEmailSubmit();
                  }
                }}
                placeholder="hello@example.com"
                disabled={verifyUserMutation.isPending}
              />
            </div>

            <button
              type="button"
              className="btn btn--primary btn--login"
              disabled={verifyUserMutation.isPending || !email.trim()}
              onClick={handleEmailSubmit}
            >
              {verifyUserMutation.isPending ? 'Checking...' : 'Continue'}
            </button>
          </div>
        ) : (
          <div className="login-form">
            <div className="form-group">
              <label htmlFor="checkout-email-display">Email</label>
              <input
                type="email"
                id="checkout-email-display"
                className="form-input"
                value={email}
                disabled
              />
            </div>

            <div className="form-group">
              <label htmlFor="checkout-password">Password</label>
              <input
                type="password"
                id="checkout-password"
                className="form-input"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handlePasswordSubmit();
                  }
                }}
                placeholder="Enter your password"
                disabled={loginMutation.isPending}
              />
            </div>

            <button
              type="button"
              className="btn btn--primary btn--login"
              disabled={loginMutation.isPending || !password.trim()}
              onClick={handlePasswordSubmit}
            >
              {loginMutation.isPending ? 'Logging in...' : 'Continue'}
            </button>

            <button
              type="button"
              className="btn btn--link btn--sm"
              onClick={() => {
                setShowPassword(false);
                setPassword('');
                setErrorMessage('');
              }}
            >
              Use a different email
            </button>
          </div>
        )}

        <div className="login-divider">
          <span>or</span>
        </div>

        <button
          type="button"
          className="btn btn--google"
          onClick={handleGoogleLogin}
          disabled={isGoogleLoading}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          {isGoogleLoading ? 'Signing in...' : 'Continue with Google'}
        </button>

        <div className="button-group">
          <p className="login-link">
            <button
              type="button"
              className="login-link-text"
              onClick={onForgotPasswordClick}
            >
              Forgot your password?
            </button>
          </p>

          <p className="login-link">
            Don't have an account?{' '}
            <button
              type="button"
              className="login-link-text"
              onClick={onRegisterClick}
            >
              Register
            </button>
          </p>
        </div>
      </div>
    </section>
  );
};

export default CheckoutLogin;
